/**
 * Agent Review — blind review queue with Mission Control chrome.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightArrowLeft,
  faArrowRotateRight,
  faClockRotateLeft,
  faExclamationTriangle,
  faFileExcel,
  faPlus,
  faRefresh,
  faSpinner,
  faTrashCan,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { MissionControlLoadingBar } from "@/components/mission-control/MissionControlLoading";
import { Button } from "@/components/ui/button/Button";
import {
  useOsActionsOptional,
  useOsNavigate,
  useSetWindowTitle,
  useWindowCloseGuard,
  useWindowId,
} from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import { ReviewWizard } from "./ReviewWizard";
import { InfrastructureFailurePanel } from "./InfrastructureFailurePanel";
import { ReviewQueueRow, ReviewQueueRowMeta, ReviewQueueRowStatus } from "./ReviewQueueRow";
import { ReviewQueueEmpty } from "./ReviewQueueEmpty";
import type { AgentRun, ArtefactRatings, PhaseRatings } from "./types";
import { agentReviewAPI, aceComparisonExportTimeoutMs, aceStabilityTestTimeoutMs, type AceComparisonExportRequest, type AceStabilityTestRequest, type ExportResultsOptions, type ReviewAnalysisResult, type ReviewSessionRecord, type RunListItem, type TaskTypeOption } from "@/services/api/agentReview";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { SearchableTaskTypeDropdown } from "./SearchableTaskTypeDropdown";
import { INTEGRATIONS_SELECT_BUTTON_CLASS } from "@/components/ops/triggers/email-triggers/integrationSelectStyles";
import { ImportTasksDialog } from "./ImportTasksDialog";
import { ClearQueueDialog } from "./ClearQueueDialog";
import { ExportResultsDialog } from "./ExportResultsDialog";
import { BackgroundAceExportBanner } from "./BackgroundAceExportBanner";
import { AceStabilityTestDialog } from "./AceStabilityTestDialog";
import { AceStabilityHistoryDialog } from "./AceStabilityHistoryDialog";
import { AceStabilityResultsPanel } from "./AceStabilityResultsPanel";
import {
  loadPersistedAceExport,
  persistAceExport,
  useAceExportBackgroundTask,
  type BackgroundAceExportState,
} from "./useAceExportBackgroundTask";
import {
  loadPersistedAceStability,
  persistAceStability,
  useAceStabilityTest,
  type BackgroundAceStabilityState,
} from "./useAceStabilityTest";
import { AgentReviewAnalysisPanel } from "./AgentReviewAnalysisPanel";
import { MoveRunsDialog } from "./MoveRunsDialog";
import { ReviewSessionsPanel } from "./ReviewSessionsPanel";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { SegmentedTabs } from "@/components/ui/segmented-tabs/SegmentedTabs";
import type { ReviewCaseOpenIntent } from "./reviewCaseOpenIntent";
import { DisabledActionTooltip } from "./DisabledActionTooltip";
import {
  clearQueueBlockedReason,
  exportResultsBlockedReason,
  moveRunsBlockedReason,
} from "./disabledActionReasons";

type QueueView = "outstanding" | "completed" | "analysis";

export interface AgentReviewDashboardProps {
  /**
   * When shown inside an aniOS window, the active case id is passed here
   * (route params are not available on the dashboard route).
   */
  windowRunId?: string;
}

function caseDisplayName(item: RunListItem): string {
  return item.subject_label?.trim() || item.label || "Case";
}

function isLoaCatalogueKey(catalogueKey: string | null): boolean {
  if (!catalogueKey) return false;
  const key = catalogueKey.toLowerCase();
  return key.includes("loa") || key.includes("octopus_generate_loas") || key.includes("octopus_rerun_loas");
}

export function AgentReviewDashboard({ windowRunId }: AgentReviewDashboardProps = {}) {
  const { runId: routeRunId } = useParams<{ runId?: string }>();
  const windowId = useWindowId();
  // In aniOS, window params are authoritative — the browser URL can still carry
  // /case/:runId after "back to list" until URL sync runs, which would resurrect
  // the wizard if we fell back to route params.
  const activeRunId = windowId
    ? (windowRunId || null)
    : (routeRunId ?? windowRunId ?? null);

  const navigate = useNavigate();
  const osNavigate = useOsNavigate();
  const osActions = useOsActionsOptional();
  const setWindowTitle = useSetWindowTitle();
  const isAdmin = useIsAdmin();

  const [activeRun, setActiveRun] = useState<AgentRun | null>(null);
  const [activeCaseLabel, setActiveCaseLabel] = useState<string | undefined>();
  const [loadingRun, setLoadingRun] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const [taskTypes, setTaskTypes] = useState<TaskTypeOption[]>([]);
  const [catalogueKey, setCatalogueKey] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ReviewSessionRecord[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsRefreshing, setRunsRefreshing] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [backgroundAceExport, setBackgroundAceExport] = useState<BackgroundAceExportState | null>(
    () => loadPersistedAceExport(),
  );
  const [backgroundAceExportSuccess, setBackgroundAceExportSuccess] = useState<string | null>(null);
  const [stabilityOpen, setStabilityOpen] = useState(false);
  const [stabilityHistoryOpen, setStabilityHistoryOpen] = useState(false);
  const [stabilityStarting, setStabilityStarting] = useState(false);
  const [stabilityError, setStabilityError] = useState<string | null>(null);
  const [backgroundAceStability, setBackgroundAceStability] = useState<BackgroundAceStabilityState | null>(
    () => loadPersistedAceStability(),
  );
  const [queueView, setQueueView] = useState<QueueView>("outstanding");
  const [analysis, setAnalysis] = useState<ReviewAnalysisResult | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(() => new Set());
  const [moveSelectMode, setMoveSelectMode] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [movingRuns, setMovingRuns] = useState(false);

  const [caseOpenIntent, setCaseOpenIntent] = useState<ReviewCaseOpenIntent | null>(null);
  const loadGeneration = useRef(0);

  const goToQueue = useCallback(
    (title = "Agent Review") => {
      setCaseOpenIntent(null);
      setActiveRun(null);
      setOpenError(null);

      if (windowId && osActions) {
        osActions.updateWindow(windowId, { params: {}, title });
      }

      if (routeRunId) {
        navigate("/ops/agent-review", { replace: true });
      } else if (!windowId) {
        navigate("/ops/agent-review");
      }
    },
    [windowId, osActions, navigate, routeRunId],
  );

  const openCase = useCallback(
    (runId: string, caseLabel?: string, intent?: ReviewCaseOpenIntent) => {
      setCaseOpenIntent(intent ?? null);
      const title = caseLabel ?? "Case";
      if (windowId && osActions) {
        osActions.updateWindow(windowId, { params: { runId }, title });
        return;
      }
      osNavigate(`/ops/agent-review/case/${encodeURIComponent(runId)}`, title);
    },
    [windowId, osActions, osNavigate],
  );

  useWindowCloseGuard(
    useCallback(() => {
      if (!activeRunId) return true;
      goToQueue();
      return false;
    }, [activeRunId, goToQueue]),
  );

  useEffect(() => {
    persistAceExport(backgroundAceExport);
  }, [backgroundAceExport]);

  useEffect(() => {
    persistAceStability(backgroundAceStability);
  }, [backgroundAceStability]);

  useAceExportBackgroundTask({
    state: backgroundAceExport,
    onUpdate: (patch) => {
      setBackgroundAceExport((current) => (current ? { ...current, ...patch } : current));
    },
    onComplete: () => {
      setBackgroundAceExport(null);
      setBackgroundAceExportSuccess("Ace export downloaded");
      setExportError(null);
    },
    onError: (message) => {
      setBackgroundAceExport(null);
      setExportError(message);
    },
  });

  useAceStabilityTest({
    state: backgroundAceStability,
    onUpdate: (patch) => {
      setBackgroundAceStability((current) => (current ? { ...current, ...patch } : current));
    },
    onComplete: () => {
      setStabilityError(null);
    },
    onError: (message) => {
      setBackgroundAceStability((current) =>
        current ? { ...current, error: message } : current,
      );
      setStabilityError(message);
    },
  });

  const refreshTaskTypes = useCallback(async () => {
    const types = await agentReviewAPI.listReviewTaskTypes();
    setTaskTypes(types);
    return types;
  }, []);

  const taskTypeOptions = useMemo<SelectOption<string>[]>(
    () => taskTypes.map((t) => ({ value: t.catalogue_key, label: t.display_name })),
    [taskTypes],
  );

  useEffect(() => {
    void refreshTaskTypes().then((types) => {
      setCatalogueKey((current) => current ?? types[0]?.catalogue_key ?? null);
    }).catch(() => {
      setRunsError("Couldn't load task types. Try refreshing.");
      setRunsLoading(false);
    });
  }, [refreshTaskTypes]);

  useEffect(() => {
    setWindowTitle(activeRunId ? (activeCaseLabel ?? "Case") : "Agent Review");
  }, [activeRunId, activeCaseLabel, setWindowTitle]);

  useEffect(() => {
    setRuns([]);
    setSessions([]);
    setSessionId(null);
    setAnalysis(null);
    setRunsError(null);
    setMoveSelectMode(false);
    setSelectedRunIds(new Set());
    setMoveDialogOpen(false);
  }, [catalogueKey]);

  const refreshSessions = useCallback(async (): Promise<ReviewSessionRecord[]> => {
    if (!catalogueKey) {
      setSessions([]);
      return [];
    }
    setSessionsLoading(true);
    try {
      const rows = await agentReviewAPI.listReviewSessions(catalogueKey);
      setSessions(rows);
      return rows;
    } catch {
      setSessions([]);
      return [];
    } finally {
      setSessionsLoading(false);
    }
  }, [catalogueKey]);

  const fetchRuns = useCallback(
    async (background = false, activeSessionId: string | null = sessionId) => {
      if (!catalogueKey || !activeSessionId) {
        return;
      }
      const generation = loadGeneration.current;
      if (background) {
        setRunsRefreshing(true);
      } else {
        setRunsLoading(true);
      }
      setRunsError(null);
      try {
        const rows = await agentReviewAPI.listRuns(catalogueKey, activeSessionId);
        if (generation !== loadGeneration.current) return;
        setRuns(rows);
      } catch {
        if (generation !== loadGeneration.current) return;
        setRunsError("Couldn't load the review queue. Try refreshing.");
      } finally {
        if (generation === loadGeneration.current) {
          setRunsLoading(false);
          setRunsRefreshing(false);
        }
      }
    },
    [catalogueKey, sessionId],
  );

  const loadTaskTypeQueue = useCallback(
    async (preferredSessionId?: string | null) => {
      if (!catalogueKey) {
        setRunsLoading(false);
        return;
      }
      const generation = ++loadGeneration.current;
      setRunsLoading(true);
      setSessionsLoading(true);
      setRunsError(null);
      try {
        const rows = await agentReviewAPI.listReviewSessions(catalogueKey);
        if (generation !== loadGeneration.current) return;
        setSessions(rows);
        const nextSessionId =
          preferredSessionId && rows.some((row) => row.session_id === preferredSessionId)
            ? preferredSessionId
            : rows[rows.length - 1]?.session_id ?? null;
        setSessionId(nextSessionId);
        if (!nextSessionId) {
          setRuns([]);
          return;
        }
        const queue = await agentReviewAPI.listRuns(catalogueKey, nextSessionId);
        if (generation !== loadGeneration.current) return;
        setRuns(queue);
      } catch {
        if (generation !== loadGeneration.current) return;
        setRunsError("Couldn't load the review queue. Try refreshing.");
        setRuns([]);
      } finally {
        if (generation === loadGeneration.current) {
          setRunsLoading(false);
          setSessionsLoading(false);
        }
      }
    },
    [catalogueKey],
  );

  useEffect(() => {
    if (!catalogueKey) {
      setRunsLoading(false);
      return;
    }
    void loadTaskTypeQueue();
  }, [catalogueKey, loadTaskTypeQueue]);

  const handleSessionChange = useCallback(
    (nextSessionId: string | null) => {
      setSessionId(nextSessionId);
      if (!catalogueKey || !nextSessionId) {
        setRuns([]);
        return;
      }
      void fetchRuns(false, nextSessionId);
    },
    [catalogueKey, fetchRuns],
  );

  useEffect(() => {
    if (!activeRunId) {
      setActiveCaseLabel(undefined);
      return;
    }
    const item = runs.find((r) => r.run_id === activeRunId);
    setActiveCaseLabel(item ? caseDisplayName(item) : undefined);
  }, [activeRunId, runs]);

  useEffect(() => {
    if (!activeRunId) {
      setActiveRun(null);
      setOpenError(null);
      return;
    }

    let cancelled = false;
    setLoadingRun(true);
    setOpenError(null);
    void agentReviewAPI
      .getRun(activeRunId)
      .then((run) => {
        if (!cancelled) {
          setActiveRun(run);
          setActiveCaseLabel((prev) => prev ?? (run.subject_label?.trim() || run.task_name || "Case"));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const isMissingCase =
            axios.isAxiosError(err) && err.response?.status === 404;
          if (isMissingCase) {
            setRunsError("That case is no longer in the queue.");
            goToQueue();
          } else {
            setOpenError("Couldn't open that case.");
          }
          setActiveRun(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRun(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeRunId, goToQueue]);

  const fetchAnalysis = useCallback(async (background = false) => {
    if (!catalogueKey || !isAdmin) return;
    if (!background) {
      setAnalysisLoading(true);
    }
    setAnalysisError(null);
    try {
      setAnalysis(await agentReviewAPI.getAnalysis(catalogueKey, sessionId));
    } catch (err) {
      setAnalysisError(err instanceof Error ? err.message : "Couldn't load analysis.");
    } finally {
      setAnalysisLoading(false);
    }
  }, [catalogueKey, sessionId, isAdmin]);

  useEffect(() => {
    if (queueView === "analysis" && isAdmin && catalogueKey) {
      void fetchAnalysis();
    }
  }, [queueView, isAdmin, catalogueKey, fetchAnalysis]);

  const reviewedCases = useMemo(
    () => runs.filter((run) => run.already_reviewed),
    [runs],
  );

  const handleExportResults = useCallback(
    async (options: ExportResultsOptions) => {
      if (!catalogueKey) return;
      setExporting(true);
      setExportError(null);
      try {
        const sessionName = sessions.find((session) => session.session_id === sessionId)?.name ?? null;
        await agentReviewAPI.downloadResultsXlsx(catalogueKey, sessionId, sessionName, options);
        setExportOpen(false);
      } catch (err) {
        setExportError(err instanceof Error ? err.message : "Export failed");
      } finally {
        setExporting(false);
      }
    },
    [catalogueKey, sessionId, sessions],
  );

  const handleExportAceComparisons = useCallback(
    async (request: AceComparisonExportRequest) => {
      if (!catalogueKey) return;
      if (backgroundAceExport) {
        setExportError("An Ace export is already running in the background.");
        return;
      }
      setExportError(null);
      setBackgroundAceExportSuccess(null);
      try {
        const queued = await agentReviewAPI.startAceComparisonExport(catalogueKey, sessionId, request);
        setBackgroundAceExport({
          taskId: queued.task_id,
          label: "Ace comparison export",
          message: queued.message ?? "Queued",
          progress: 0,
          startedAt: Date.now(),
          maxWaitMs: aceComparisonExportTimeoutMs(
            request.runIds.length,
            request.aceProfiles.length,
            catalogueKey,
          ),
        });
        setExportOpen(false);
      } catch (err) {
        setExportError(err instanceof Error ? err.message : "Ace comparison export failed");
      }
    },
    [catalogueKey, sessionId, backgroundAceExport],
  );

  const handleStartAceStabilityTest = useCallback(
    async (request: AceStabilityTestRequest) => {
      if (!catalogueKey) return;
      if (backgroundAceStability && !backgroundAceStability.result && !backgroundAceStability.error) {
        setStabilityError("An Ace stability test is already running.");
        return;
      }
      setStabilityError(null);
      setStabilityStarting(true);
      try {
        const caseLabel =
          reviewedCases.find((item) => item.run_id === request.runId)?.subject_label?.trim() ||
          reviewedCases.find((item) => item.run_id === request.runId)?.label?.trim() ||
          request.runId.slice(-8);
        const queued = await agentReviewAPI.startAceStabilityTest(catalogueKey, sessionId, request);
        setBackgroundAceStability({
          taskId: queued.task_id,
          caseLabel,
          message: queued.message ?? "Queued",
          progress: 0,
          startedAt: Date.now(),
          executionStartedAt: null,
          iterations: request.iterations,
          maxWaitMs: aceStabilityTestTimeoutMs(request.iterations, catalogueKey),
          result: null,
          error: null,
          trackingPaused: false,
          blockingTaskLabel: null,
        });
        setStabilityOpen(false);
      } catch (err) {
        setStabilityError(err instanceof Error ? err.message : "Ace stability test failed");
      } finally {
        setStabilityStarting(false);
      }
    },
    [catalogueKey, sessionId, backgroundAceStability, reviewedCases],
  );

  const handleExportCalibration = useCallback(async () => {
    if (!catalogueKey) return;
    setExporting(true);
    setExportError(null);
    try {
      const sessionName = sessions.find((session) => session.session_id === sessionId)?.name ?? null;
      await agentReviewAPI.downloadCalibrationXlsx(catalogueKey, sessionId, sessionName);
      setExportOpen(false);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Calibration export failed");
    } finally {
      setExporting(false);
    }
  }, [catalogueKey, sessionId, sessions]);

  const exitMoveSelectMode = useCallback(() => {
    setMoveSelectMode(false);
    setSelectedRunIds(new Set());
    setMoveDialogOpen(false);
  }, []);

  const handleMoveRuns = useCallback(
    async (runIds: string[], targetSessionId: string) => {
      if (!targetSessionId || targetSessionId === sessionId || runIds.length === 0) return;
      setMovingRuns(true);
      setRunsError(null);
      try {
        const result = await agentReviewAPI.moveRunsToSession(runIds, targetSessionId);
        if (result.moved_count === 0) {
          setRunsError("Couldn't move the selected cases — they may no longer be in this session.");
          return;
        }
        exitMoveSelectMode();
        await refreshSessions();
        void fetchRuns(true);
      } catch {
        setRunsError("Couldn't move the selected cases to another session.");
      } finally {
        setMovingRuns(false);
      }
    },
    [sessionId, refreshSessions, fetchRuns, exitMoveSelectMode],
  );

  const sessionStats = useMemo(() => {
    const total = runs.length;
    const reviewed = runs.filter((r) => r.already_reviewed).length;
    return { reviewed, total, outstanding: total - reviewed };
  }, [runs]);

  const visibleRuns = useMemo(
    () =>
      queueView === "completed"
        ? runs.filter((r) => r.already_reviewed)
        : runs.filter((r) => !r.already_reviewed),
    [runs, queueView],
  );

  const canBulkMove = isAdmin && sessions.length > 1 && queueView !== "analysis";
  const canShowMoveButton = canBulkMove && visibleRuns.length > 0;

  const otherSessionOptions = useMemo<SelectOption<string>[]>(
    () =>
      sessions
        .filter((session) => session.session_id !== sessionId)
        .map((session) => ({ value: session.session_id, label: session.name })),
    [sessions, sessionId],
  );

  useEffect(() => {
    exitMoveSelectMode();
  }, [catalogueKey, sessionId, queueView, exitMoveSelectMode]);

  const toggleRunSelection = useCallback((runId: string) => {
    setSelectedRunIds((current) => {
      const next = new Set(current);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  }, []);

  const selectAllVisibleRuns = useCallback(() => {
    setSelectedRunIds(new Set(visibleRuns.map((run) => run.run_id)));
  }, [visibleRuns]);

  const clearRunSelection = useCallback(() => {
    setSelectedRunIds(new Set());
  }, []);

  const handleSubmit = async (run: AgentRun, ratings: PhaseRatings, artefactRatings: ArtefactRatings) => {
    const record = await agentReviewAPI.submitReview({ run, ratings, artefactRatings });
    setActiveRun((prev) => (prev && prev.run_id === run.run_id ? { ...prev, existing_review: record } : prev));
    void fetchRuns(true);
    if (queueView === "analysis") {
      void fetchAnalysis(true);
    }
    return record;
  };

  const taskTypeLabel =
    taskTypeOptions.find((t) => t.value === catalogueKey)?.label ?? "this task type";

  const queueLoading = runsLoading && runs.length === 0;
  const queueEmpty = !queueLoading && !runsError && runs.length === 0;

  if (activeRunId && activeRun) {
    if (activeRun.infrastructure_failure) {
      return (
        <InfrastructureFailurePanel
          key={activeRun.run_id}
          run={activeRun}
          caseLabel={activeCaseLabel}
          onDone={() => goToQueue()}
        />
      );
    }

    return (
      <ReviewWizard
        key={activeRun.run_id}
        run={activeRun}
        caseLabel={activeCaseLabel}
        catalogueKey={catalogueKey}
        sessionStats={sessionStats}
        existingReview={activeRun.existing_review ?? null}
        initialOpenIntent={caseOpenIntent}
        onOpenIntentHandled={() => setCaseOpenIntent(null)}
        onDone={() => goToQueue()}
        onSubmit={(ratings, artefactRatings) => handleSubmit(activeRun, ratings, artefactRatings)}
      />
    );
  }

  return (
    <div className={clsx("flex flex-col h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)} data-testid="agent-review-dashboard">
      <div className={clsx("relative flex-1 min-h-0 flex flex-col rounded-2xl px-6 py-5", WORKBENCH_SURFACE_CLASS)}>
        <div className="flex flex-col gap-3 mb-3 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {taskTypeOptions.length > 0 && catalogueKey ? (
              <div className="mr-auto w-full min-w-[12rem] max-w-sm sm:w-auto">
                <SearchableTaskTypeDropdown
                  inline
                  buttonClassName={INTEGRATIONS_SELECT_BUTTON_CLASS}
                  options={taskTypeOptions}
                  value={catalogueKey}
                  onChange={setCatalogueKey}
                  data-testid="agent-review-task-type"
                />
              </div>
            ) : sessionStats.total > 0 ? (
              <span className="text-xs text-slate-500 tabular-nums mr-auto">
                {sessionStats.reviewed} of {sessionStats.total} reviewed
              </span>
            ) : null}
          {sessionStats.total > 0 && taskTypeOptions.length > 0 ? (
            <span className="text-xs text-slate-500 tabular-nums">
              {sessionStats.reviewed} of {sessionStats.total} reviewed
            </span>
          ) : null}
          {isAdmin ? (
            <>
              <DisabledActionTooltip
                reasons={[
                  exportResultsBlockedReason({
                    catalogueKey,
                    reviewedCount: sessionStats.reviewed,
                    exporting,
                  }),
                ].filter((r): r is string => Boolean(r))}
                heading="Can't export yet"
              >
                <Button
                  plain
                  onClick={() => {
                    setExportError(null);
                    setExportOpen(true);
                  }}
                  disabled={!catalogueKey || sessionStats.reviewed === 0 || exporting}
                  className="!text-slate-300 hover:!text-white disabled:!opacity-40"
                >
                  <FontAwesomeIcon icon={faFileExcel} />
                  Export…
                </Button>
              </DisabledActionTooltip>
              <DisabledActionTooltip
                reasons={[
                  !catalogueKey ? "Select a task type first." : null,
                  sessionStats.reviewed === 0 ? "Review at least one case first." : null,
                  backgroundAceStability && !backgroundAceStability.result && !backgroundAceStability.error
                    ? "An Ace stability test is already running."
                    : null,
                ].filter((r): r is string => Boolean(r))}
                heading="Can't run stability test yet"
              >
                <Button
                  plain
                  onClick={() => {
                    setStabilityError(null);
                    setStabilityOpen(true);
                  }}
                  disabled={
                    !catalogueKey ||
                    sessionStats.reviewed === 0 ||
                    Boolean(
                      backgroundAceStability &&
                        !backgroundAceStability.result &&
                        !backgroundAceStability.error,
                    )
                  }
                  className="!text-slate-300 hover:!text-white disabled:!opacity-40"
                  data-testid="agent-review-ace-stability"
                >
                  <FontAwesomeIcon icon={faArrowRotateRight} />
                  Ace stability…
                </Button>
              </DisabledActionTooltip>
              <Button
                plain
                onClick={() => setStabilityHistoryOpen(true)}
                disabled={!catalogueKey}
                className="!text-slate-300 hover:!text-white disabled:!opacity-40"
                data-testid="agent-review-ace-stability-history"
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
                Stability history
              </Button>
              <DisabledActionTooltip
                reasons={[
                  clearQueueBlockedReason({
                    catalogueKey,
                    outstandingCount: sessionStats.outstanding,
                  }),
                ].filter((r): r is string => Boolean(r))}
                heading="Can't clear yet"
              >
                <Button
                  plain
                  onClick={() => setClearOpen(true)}
                  disabled={!catalogueKey || sessionStats.outstanding === 0}
                  className="!text-slate-300 hover:!text-white disabled:!opacity-40"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                  Clear to review
                </Button>
              </DisabledActionTooltip>
              <Button plain onClick={() => setImportOpen(true)} className="!text-slate-300 hover:!text-white">
                <FontAwesomeIcon icon={faPlus} />
                Import tasks
              </Button>
              {canShowMoveButton ? (
                moveSelectMode ? (
                  <DisabledActionTooltip
                    reasons={[moveRunsBlockedReason(selectedRunIds.size)].filter(
                      (r): r is string => Boolean(r),
                    )}
                    heading="Can't move yet"
                  >
                    <Button
                      onClick={() => setMoveDialogOpen(true)}
                      disabled={selectedRunIds.size === 0}
                      className="!h-9"
                    >
                      <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                      Move to session…
                    </Button>
                  </DisabledActionTooltip>
                ) : (
                  <Button
                    plain
                    onClick={() => setMoveSelectMode(true)}
                    className="!text-slate-300 hover:!text-white"
                    data-testid="agent-review-select-tasks-to-move"
                  >
                    <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                    Move between sessions
                  </Button>
                )
              ) : null}
            </>
          ) : null}
          <Button
            plain
            onClick={() => {
              void fetchRuns(true);
              if (queueView === "analysis") {
                void fetchAnalysis(true);
              }
            }}
            className="!text-slate-300 hover:!text-white"
          >
            <FontAwesomeIcon icon={faRefresh} />
            Refresh
          </Button>
          </div>
          {catalogueKey ? (
            <ReviewSessionsPanel
              catalogueKey={catalogueKey}
              sessionId={sessionId}
              sessions={sessions}
              sessionsLoading={sessionsLoading}
              taskTypeLabel={taskTypeLabel}
              canDelete={isAdmin}
              onSessionIdChange={handleSessionChange}
              onSessionsUpdated={async () => {
                await loadTaskTypeQueue(sessionId);
              }}
            />
          ) : null}
        </div>
        {backgroundAceStability ? (
          <AceStabilityResultsPanel
            state={backgroundAceStability}
            catalogueKey={catalogueKey}
            onDismiss={() => setBackgroundAceStability(null)}
            onViewHistory={() => setStabilityHistoryOpen(true)}
            onRequeue={() => {
              setBackgroundAceStability((current) =>
                current
                  ? {
                      ...current,
                      republishAttempted: true,
                      waitingForWorker: true,
                      queuedBehindAnother: false,
                      blockingTaskLabel: null,
                      message: "Re-queued — waiting for worker to pick up the job…",
                    }
                  : current,
              );
            }}
          />
        ) : null}
        {backgroundAceExport ? (
          <BackgroundAceExportBanner exportState={backgroundAceExport} />
        ) : backgroundAceExportSuccess ? (
          <BackgroundAceExportBanner
            successMessage={backgroundAceExportSuccess}
            onDismiss={() => setBackgroundAceExportSuccess(null)}
          />
        ) : null}
        {exportError ? <p className="text-sm text-rose-400 mb-2">{exportError}</p> : null}

        <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
          <MissionControlLoadingBar
            active={
              queueLoading ||
              runsRefreshing ||
              loadingRun ||
              (queueView === "analysis" && analysisLoading)
            }
          />

          {loadingRun && activeRunId ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-slate-900/40 backdrop-blur-[2px]">
              <p className="text-sm text-slate-300">Opening case…</p>
            </div>
          ) : null}

          {openError && <p className="text-sm text-rose-400 text-center py-2">{openError}</p>}

          <div
            className={clsx(
              "flex min-h-0 flex-1 flex-col overflow-hidden transition-opacity duration-200",
              (queueLoading ||
                runsRefreshing ||
                loadingRun ||
                (queueView === "analysis" && analysisLoading)) &&
                "pointer-events-none opacity-60",
            )}
          >
            <SegmentedTabs
              className="mb-3 flex-shrink-0"
              activeId={queueView}
              onChange={(id) => setQueueView(id as QueueView)}
              tabs={[
                {
                  id: "outstanding",
                  label: "To review",
                  count: sessionStats.outstanding,
                  countVariant: sessionStats.outstanding > 0 ? "attention" : "neutral",
                },
                {
                  id: "completed",
                  label: "Reviewed",
                  count: sessionStats.reviewed,
                },
                ...(isAdmin
                  ? [
                      {
                        id: "analysis" as const,
                        label: "Calibration",
                        count: analysis?.open_dispute_count ?? undefined,
                        countVariant:
                          (analysis?.open_dispute_count ?? 0) > 0
                            ? ("attention" as const)
                            : ("neutral" as const),
                      },
                    ]
                  : []),
              ]}
            />

            {queueView === "analysis" ? (
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1">
                <AgentReviewAnalysisPanel
                  analysis={analysis}
                  loading={analysisLoading}
                  error={analysisError}
                  catalogueKey={catalogueKey}
                  sessionId={sessionId}
                  onOpenCase={(runId, label) => openCase(runId, label)}
                  onRefresh={() => void fetchAnalysis()}
                  onExportCalibration={() => void handleExportCalibration()}
                  exportingCalibration={exporting}
                />
              </div>
            ) : queueLoading ? (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-slate-400"
                data-testid="agent-review-queue-loading"
              >
                <FontAwesomeIcon icon={faSpinner} className="text-xl animate-spin text-indigo-400" />
                <p className="text-sm">Loading queue…</p>
              </div>
            ) : runsError ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-slate-300">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 text-2xl" />
                <p className="text-sm">{runsError}</p>
                <Button onClick={() => void loadTaskTypeQueue(sessionId)}>Retry</Button>
              </div>
            ) : queueEmpty ? (
              queueView === "completed" ? (
                <p className="text-sm text-slate-500 py-8 text-center">No reviewed cases yet.</p>
              ) : (
                <ReviewQueueEmpty allReviewed={false} />
              )
            ) : visibleRuns.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">
                {queueView === "completed"
                  ? "No reviewed cases yet."
                  : "All cases reviewed — switch to Reviewed or import more."}
              </p>
            ) : (
              <>
                {moveSelectMode && canBulkMove ? (
                  <div
                    className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-indigo-500/10 px-3 py-2 ring-1 ring-indigo-400/25"
                    data-testid="agent-review-bulk-move-bar"
                  >
                    <p className="text-sm text-indigo-100">
                      Click tasks below to select them
                    </p>
                    <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
                      <button
                        type="button"
                        className="text-indigo-200 hover:text-white"
                        onClick={selectAllVisibleRuns}
                      >
                        Select all
                      </button>
                      <span className="text-slate-600">·</span>
                      <button
                        type="button"
                        className="text-indigo-200 hover:text-white disabled:opacity-40"
                        disabled={selectedRunIds.size === 0}
                        onClick={clearRunSelection}
                      >
                        Unselect all
                      </button>
                      <span className="text-slate-600">·</span>
                      <button
                        type="button"
                        className="text-slate-300 hover:text-white"
                        onClick={exitMoveSelectMode}
                      >
                        Cancel
                      </button>
                      <span className="ml-1 tabular-nums text-slate-400">
                        {selectedRunIds.size} selected
                      </span>
                    </div>
                  </div>
                ) : null}
                <ul className="flex flex-col gap-2 overflow-y-auto min-h-0">
                {visibleRuns.map((item) => (
                  <li
                    key={item.run_id}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-1"
                  >
                    <ReviewQueueRow
                      variant="queue"
                      caseLabel={item.label ?? "Case"}
                      subjectLabel={item.subject_label}
                      summarySnippet={item.summary_snippet}
                      taskName={item.task_name}
                      occurredAt={item.occurred_at}
                      stepCount={item.step_count}
                      reviewed={item.already_reviewed}
                      infrastructureFailure={item.infrastructure_failure}
                      selectionMode={moveSelectMode}
                      selected={selectedRunIds.has(item.run_id)}
                      onToggleSelect={() => toggleRunSelection(item.run_id)}
                      onOpen={() => openCase(item.run_id, caseDisplayName(item))}
                    />
                    <ReviewQueueRowMeta
                      caseLabel={item.label ?? "Case"}
                      subjectLabel={item.subject_label}
                      occurredAt={item.occurred_at}
                      stepCount={item.step_count}
                    />
                    <ReviewQueueRowStatus
                      reviewed={item.already_reviewed}
                      infrastructureFailure={item.infrastructure_failure}
                    />
                  </li>
                ))}
              </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {isAdmin ? (
        <>
          <ExportResultsDialog
            open={exportOpen}
            taskTypeLabel={taskTypeLabel}
            reviewedCount={sessionStats.reviewed}
            reviewedCases={reviewedCases}
            showLoaOption={isLoaCatalogueKey(catalogueKey)}
            showCalibrationTab
            calibrationDisputeCount={analysis?.check_disagreements.length ?? 0}
            exporting={exporting}
            error={exportError}
            onClose={() => setExportOpen(false)}
            onExportResults={(options) => void handleExportResults(options)}
            onExportAceComparisons={(request) => void handleExportAceComparisons(request)}
            onExportCalibration={() => void handleExportCalibration()}
          />
          <AceStabilityTestDialog
            open={stabilityOpen}
            taskTypeLabel={taskTypeLabel}
            reviewedCases={reviewedCases}
            running={stabilityStarting}
            error={stabilityError}
            onClose={() => setStabilityOpen(false)}
            onStart={(request) => void handleStartAceStabilityTest(request)}
          />
          <AceStabilityHistoryDialog
            open={stabilityHistoryOpen}
            catalogueKey={catalogueKey ?? ""}
            sessionId={sessionId}
            onClose={() => setStabilityHistoryOpen(false)}
            onTrackRun={(state) => {
              setBackgroundAceStability(state);
              setStabilityError(null);
            }}
          />
          <ClearQueueDialog
            open={clearOpen}
            catalogueKey={catalogueKey}
            sessionId={sessionId}
            taskTypeLabel={taskTypeLabel}
            outstandingCount={sessionStats.outstanding}
            reviewedCount={sessionStats.reviewed}
            onClose={() => setClearOpen(false)}
            onCleared={async () => {
              const types = await refreshTaskTypes();
              if (catalogueKey && !types.some((t) => t.catalogue_key === catalogueKey)) {
                setCatalogueKey(types[0]?.catalogue_key ?? null);
              }
              await refreshSessions();
              void fetchRuns(true);
            }}
          />
          <ImportTasksDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            defaultCatalogueKey={catalogueKey}
            sessionId={sessionId}
            onImported={async (result, importedCatalogueKey) => {
              await refreshTaskTypes();
              if (result.imported_count > 0 && importedCatalogueKey !== catalogueKey) {
                setCatalogueKey(importedCatalogueKey);
                return;
              }
              await loadTaskTypeQueue(sessionId);
            }}
          />
          <MoveRunsDialog
            open={moveDialogOpen}
            selectedCount={selectedRunIds.size}
            sessions={otherSessionOptions}
            moving={movingRuns}
            onClose={() => setMoveDialogOpen(false)}
            onConfirm={(targetSessionId) => void handleMoveRuns([...selectedRunIds], targetSessionId)}
          />
        </>
      ) : null}
    </div>
  );
}

export default AgentReviewDashboard;
